import { useEffect, useLayoutEffect } from "react"

import { Typography } from "antd"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"

import { getDates } from "../../Utils/utils"
import AlertsBySourceTable from "./components/AlertsBySourceTable/AlertsBySourceTable"
import MileStones from "./components/MileStones"
import ReferralsOverviewChart from "./components/OverviewChart"
import { ReferralFilterBlock } from "./components/OverviewChartFilters"
import OverviewTotalReward from "./components/OverviewTotalReward"
import ReferralsTable from "./components/ReferralsTable"
import TotalNumbers from "./components/TotalNumbers"
import EarningsStats from "./Outsiders/components/EarningsStats"
import ReferralActions from "./reducer"

import "./styles.less"

const { Text } = Typography

const Earnings = () => {
  const dispatch = useDispatch()
  const { id } = useParams()
  const { isAdmin, userInfo } = useSelector((state) => state.auth)
  const isMobile = useSelector((state) => state.nav.isMobile)

  const {
    earningsFilters,
    overviewMainInfo,
    alertsBySourceTable,
    alertsBySourceFetching,
    referralBAOptions,
    affiliateAccountInfo
  } = useSelector((state) => state.referrals)

  const isExternalReferral = isAdmin
    ? affiliateAccountInfo?.referral_outsider
    : userInfo?.referral_outsider

  const adminParams = isAdmin && id ? { user_id: id } : {}

  const setFilters = (filters) => {
    if (Array.isArray(filters?.period)) {
      dispatch(
        ReferralActions.setEarningsFilters({
          __preset_period: filters?.__preset_period,
          period: { type: "custom", period: filters?.period }
        })
      )
    } else {
      dispatch(ReferralActions.setEarningsFilters(filters))
    }
  }

  useLayoutEffect(() => {
    dispatch(ReferralActions.getReferralsTableRequest(adminParams))
    dispatch(ReferralActions.getTotalStatisticsRequest(adminParams))
    dispatch(ReferralActions.getReferralBAOptionsRequest(adminParams))
  }, [])

  useEffect(() => {
    if (isAdmin && id) {
      dispatch(ReferralActions.getAffiliateAccountInfoRequest(adminParams))
    }
  }, [isAdmin, id])

  useEffect(() => {
    const period_query = getDates(earningsFilters.period)
    const businessAccountsQuery =
      earningsFilters.business_accounts === "all" || !earningsFilters.business_accounts
        ? null
        : { business_account_id: earningsFilters.business_accounts.join(",") }
    dispatch(
      ReferralActions.getReferralsOverviewDataRequest({
        ...period_query,
        ...businessAccountsQuery,
        ...adminParams
      })
    )
    dispatch(
      ReferralActions.getReferralsOverviewMainInfoRequest({
        ...period_query,
        ...businessAccountsQuery,
        ...adminParams
      })
    )
    dispatch(
      ReferralActions.getAlertsBySourceTableRequest({
        ...period_query,
        ...businessAccountsQuery,
        ...adminParams
      })
    )
  }, [earningsFilters])

  return (
    <div className="referral-earnings">
      {isExternalReferral ? <EarningsStats /> : <TotalNumbers />}
      <MileStones />
      <ReferralsTable />
      <Text className="overview-title">Overview</Text>
      {!isMobile && (
        <ReferralFilterBlock
          filters={earningsFilters}
          handleChangeFilter={setFilters}
          businessAccounts={referralBAOptions}
          isMobile={isMobile}
        />
      )}
      <OverviewTotalReward
        title={!isMobile ? "Alerts Overview" : null}
        reward={overviewMainInfo?.total_reward ?? 0}
        count={overviewMainInfo?.total_count ?? 0}
        tooltip={overviewMainInfo?.tooltip}
        style={{ marginBottom: 22 }}
        isMobile={isMobile}
      />
      {isMobile && (
        <ReferralFilterBlock
          filters={earningsFilters}
          handleChangeFilter={setFilters}
          businessAccounts={referralBAOptions}
          isMobile={isMobile}
        />
      )}
      <ReferralsOverviewChart />
      <OverviewTotalReward
        title="Alerts by Source"
        reward={overviewMainInfo?.total_reward ?? 0}
        count={overviewMainInfo?.total_count ?? 0}
        tooltip={overviewMainInfo?.tooltip}
        style={{
          marginBottom: isMobile ? 16 : 42,
          marginTop: isMobile ? 16 : 48
        }}
        isMobile={isMobile}
      />
      <AlertsBySourceTable data={alertsBySourceTable} loading={alertsBySourceFetching} />
    </div>
  )
}

export default Earnings
